<script setup lang="ts">
/**
 * «Недавняя активность» — макет 17:765: события по всем видимым проектам.
 *
 * Фраза собирается здесь, а не в сервисе: тот отдаёт факт (что произошло, с
 * какой задачей, кто сделал), а перевод живёт в словаре — иначе лента на
 * английской локали осталась бы русской.
 */
import { GitMerge, MessageSquare, MoveRight, Plus, Rocket } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { formatStamp } from '@/features/issues/format'

import { kindKey, type ActivityEvent } from '../activity'

const props = defineProps<{ events: ActivityEvent[] }>()

const { t, locale } = useI18n()

const ICONS = {
  created: Plus,
  moved: MoveRight,
  comment: MessageSquare,
  merged: GitMerge,
  devops: Rocket,
  other: Plus,
}

const rows = computed(() =>
  props.events.map((event) => {
    const key = kindKey(event.kind)

    return {
      key: `${event.issueKey}-${event.createdAt}-${key}`,
      icon: ICONS[key as keyof typeof ICONS] ?? ICONS.other,
      issueKey: event.issueKey,
      text: t(`hub.activity.kinds.${key}`, {
        actor: event.actorName || t('hub.activity.someone'),
        subject: event.subject,
      }),
      when: formatStamp(event.createdAt, locale.value, {
        today: t('issue.today'),
        yesterday: t('issue.yesterday'),
      }),
    }
  }),
)
</script>

<template>
  <section class="feed">
    <h2 class="feed__title">{{ t('hub.activity.title') }}</h2>

    <p v-if="!rows.length" class="feed__empty">{{ t('hub.activity.empty') }}</p>

    <RouterLink
      v-for="row in rows"
      :key="row.key"
      class="feed__row"
      :to="row.issueKey ? `/issue/${row.issueKey}` : '/projects'"
    >
      <component :is="row.icon" class="feed__icon" :size="14" />
      <span v-if="row.issueKey" class="feed__key">{{ row.issueKey }}</span>
      <span class="feed__text">{{ row.text }}</span>
      <span class="feed__when">{{ row.when }}</span>
    </RouterLink>
  </section>
</template>

<style scoped>
.feed {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.feed__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
}

.feed__empty {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.feed__row {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 12px;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.feed__row:hover {
  background: var(--color-bg-muted);
}

.feed__icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.feed__key {
  flex-shrink: 0;
  color: var(--color-text-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
}

.feed__text {
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.feed__when {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}
</style>
